import type { AuthState } from '@/app/providers/auth'
import { useAppError } from '@/components/error-page/errors'
import type {
  GetVendorAvailabilityListQuery,
  MenuAvailabilityWithItemsPagedFragment,
} from '@/generated/graphql'
import { useGetVendorAvailabilityListLazyQuery } from '@/generated/graphql'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getDateTimeObject } from '../../common/domain/user-basket-settings'
import { useAppStateStore } from '../../common/store/appStateStore'
import type { MenuAvailability } from '../domain/menu-availability'
import { createMenuAvailability } from '../domain/menu-availability'
import type { SelectedMenuFilterCategory } from '../domain/menu-category-filter'
import type { MenuItemFilterSections } from '../domain/menu-item-filter'
import type { MenuVendorCategory } from '../domain/menu-vendor'
import { useGmFiltersState } from '../store/gmFiltersStore'
import { useVendorLocationData } from '../store/vendorLocationStore'
import { resolveVendorMenuTimezone } from './vendorMenuTimezone'

export type LoadedMenu = {
  type: 'loaded'
  availability: MenuAvailability
  hasMore: boolean
  reloading: boolean
  loadMore: () => void
  loadingMore: boolean
}

type PageState = { type: 'loading' } | LoadedMenu

export function useMenuPageState({
  itemData,
  useServerSideItems,
  vendor,
  authState,
}: {
  itemData?: GetVendorAvailabilityListQuery
  useServerSideItems?: boolean
  authState: AuthState
  vendor: {
    id: string
    permalink: string
    categories: MenuVendorCategory[]
    locations: { id: string; timezone: string }[]
  }
}): PageState {
  const { throwAppError } = useAppError()
  const { userState } = useAppStateStore()
  const { selectedVendorLocation } = useVendorLocationData()
  const vendorTimezone = resolveVendorMenuTimezone(vendor.locations, selectedVendorLocation)

  const { itemName, selectedCategories, goals, allergens, dietaries } = useGmFiltersState()

  const [runGetAvailability, getAvailabilityResult] = useGetVendorAvailabilityListLazyQuery()
  const rawAvailability =
    useServerSideItems && itemData
      ? itemData.availability
      : (getAvailabilityResult.data?.availability ??
        getAvailabilityResult.previousData?.availability)

  const availability = useMemo(() => {
    return rawAvailability
      ? createMenuAvailability({
          availability: {
            ...rawAvailability,
            itemsPagedv2: rawAvailability?.itemsPagedv2 || [],
          } as MenuAvailabilityWithItemsPagedFragment,
          deliveryDate: userState.date ? new Date(userState.date).getTime() : undefined,
          vendorCategories: vendor.categories,
          vendorTimezone,
          selectedCategories,
          useServerSideItems,
          authState,
          accountId:
            authState.type === 'authenticated' && authState.user?.account?.id
              ? Number(authState.user.account.id)
              : null,
          filters: {
            itemName,
            goals,
            dietaries,
            allergens,
          },
        })
      : null
  }, [
    rawAvailability,
    selectedCategories,
    itemName,
    goals,
    dietaries,
    allergens,
    authState,
    useServerSideItems,
    vendor.categories,
    userState.date,
    vendorTimezone,
  ])

  const [loadingMore, setLoadingMore] = useState(false)

  const loadMore = useCallback(async () => {
    if (useServerSideItems) return
    setLoadingMore(true)
    await getAvailabilityResult.fetchMore({
      variables: {
        offset: availability?.items.length,
        limit: 20,
      },
    })
    setLoadingMore(false)
  }, [getAvailabilityResult, availability?.items.length, useServerSideItems])

  const constructFiltersForQuery = useCallback(
    (filters: MenuItemFilterSections, category: SelectedMenuFilterCategory) => {
      const selectedDietaries = [
        ...filters.goals.map((goal) => goal.filterInputValue),
        ...filters.dietaries.reduce<string[]>((dietaries, dietary) => {
          if (dietary.filterArray !== 'dietaries') return dietaries
          return [...dietaries, dietary.filterInputValue]
        }, []),
      ]
      const selectedAllergens = [
        ...filters.allergens.map((allergen) => allergen.value),
        ...filters.dietaries.reduce<string[]>((dietaries, dietary) => {
          if (dietary.filterArray !== 'allergens') return dietaries
          return [...dietaries, dietary.filterInputValue]
        }, []),
      ]
      const categoryId = Number(category.id)
      const dateTimeObject = getDateTimeObject({ date: userState.date, time: userState.time })
      return {
        allergens: selectedAllergens,
        dietaries: selectedDietaries,
        ...(!Number.isNaN(categoryId) && { categoryId: [categoryId] }),
        ...(category.id === 'hot_dishes' && { isHot: true }),
        ...(category.id === 'cold_dishes' && { isHot: false }),
        name: itemName,
        ...(dateTimeObject &&
          vendorTimezone && {
            deliveryDate: { ...dateTimeObject, timezone: vendorTimezone },
          }),
      }
    },
    [itemName, userState.date, userState.time, vendorTimezone],
  )

  useEffect(() => {
    if (useServerSideItems) return
    const sortedCategoryIds = [...vendor.categories]
      .sort((a, b) => {
        if (a.position === null) return 1
        if (b.position === null) return -1
        return a.position - b.position
      })
      .map((category) => Number(category.id))

    const constructedFilters = constructFiltersForQuery(
      {
        goals,
        allergens,
        dietaries,
      },
      selectedCategories[0] || { id: 'everything', name: 'Everything' },
    )
    void runGetAvailability({
      variables: {
        vendorId: vendor.id,
        filters: constructedFilters,
        sorting: { categorySortPriority: sortedCategoryIds },
        offset: 0,
        limit: 20,
      },
    })
  }, [
    vendor,
    itemName,
    selectedCategories,
    goals,
    allergens,
    dietaries,
    runGetAvailability,
    constructFiltersForQuery,
    useServerSideItems,
  ])

  const initialPageState: PageState = {
    type: 'loading',
  }
  if (
    !useServerSideItems &&
    (!getAvailabilityResult.called ||
      (getAvailabilityResult.loading && !getAvailabilityResult?.previousData))
  ) {
    return initialPageState
  }

  if (!availability)
    return throwAppError({
      type: 'COULD_NOT_FETCH_AVAILABILITY_FOR_VENDOR',
      vendorId: vendor.id,
      cause: getAvailabilityResult.error,
    })

  return {
    ...initialPageState,
    type: 'loaded',
    reloading: getAvailabilityResult.loading,
    availability,
    hasMore: Boolean(getAvailabilityResult.data?.availability?.itemsPagedv2.pageInfo.hasMore),
    loadMore,
    loadingMore,
  }
}
